import React from "react";
import { LogEvent } from "../models/LogEvents";
import { EventCard } from "./EventCard";
import { ViewJsonInPage } from "./ViewJsonInPage";
import { tryJsonParse, camelToTitleCase, openLinkInNewTab, generateTableHeight } from "../utils";
import { Typography, MenuItem } from "@material-ui/core";
import { Fetcher } from "./Fetcher";
import { ViewJsonDialog } from "./ViewJsonDialog";
import { EVENT_CARD_TITLES, MIN_TABLE_HEIGHT } from "../constants";
import { getFlowExecutionUrl } from "../utils/externalLinks";
import { CellMeasurer, CellMeasurerCache, List, AutoSizer } from "react-virtualized";

let list;
let cache;

interface LogListContainerProps {
	//A list of LogEvent type objects used to create the EventCards.
	logs: LogEvent[];

	//A function that is optionally passed in to pass to cards for rendering the details page.
	renderDetailsFunction?: (sid, open: boolean) => string;

	//Callback method to be called when message not included with log and must be retreived via a seperate API call.
	loadJson?: (sid: string) => () => Promise<any>;
}

const secondaryFunction = (source: string, innerProperties: object) => {
	const sourceType = EVENT_CARD_TITLES[source];
	switch (sourceType) {
		case EVENT_CARD_TITLES.flowExecutionStep: {
			const context = innerProperties["context"];
			delete innerProperties["context"];
			return context;
		}
	}
};

const secondaryFunctionTitle = (source: string) => {
	const sourceType = EVENT_CARD_TITLES[source];
	switch (sourceType) {
		case EVENT_CARD_TITLES.flowExecutionStep: {
			return "Flow Context";
		}
	}
};

const getExternalLinks = (log: LogEvent, innerProperties: object) => {
	const buttons: React.ReactNode[] = [];
	if (log.source == "flowExecutionStep") {
		buttons.push(
			<MenuItem
				key={"Flow Execution Details Link"}
				onClick={() => openLinkInNewTab(getFlowExecutionUrl(log["flowSid"], log["executionSid"]))}
			>
				Flow Execution Details
			</MenuItem>,
		);
		delete innerProperties["flowSid"];
	}
	return buttons;
};

const messageViewer = (
	source: string,
	message: string,
	index: number,
	sid?: string,
	loadJson?: (sid: string) => () => Promise<any>,
	virtualized?: boolean,
) => {
	return message ? (
		<ViewJsonInPage title={camelToTitleCase(source)} json={tryJsonParse(message)}></ViewJsonInPage>
	) : loadJson ? (
		<Fetcher
			action={() => {
				if (virtualized) {
					cache.clear(index, 0);
					list.recomputeRowHeights(index);
				}
				return loadJson(sid!)();
			}}
		>
			{(response) => {
				if (virtualized) {
					cache.clear(index, 0);
					list.recomputeRowHeights(index);
				}
				return <ViewJsonInPage json={response.data} title={camelToTitleCase(source)}></ViewJsonInPage>;
			}}
		</Fetcher>
	) : (
		<Typography>No message or json load action found for log.</Typography>
	);
};

const Row = (props: {
	log: LogEvent;
	expandCard: (expand: boolean) => void;
	expanded: boolean;
	detailsFunction?: (sid, open: boolean) => string;
	index: number;
	loadJson?: (sid: string) => () => Promise<any>;
	virtualized?: boolean;
}) => {
	const { message, source, eventDate, sid, ...innerProperties } = props.log;

	const secondaryFunctionValue = secondaryFunction(source, innerProperties);
	const secondaryFunctionValueTitle = secondaryFunctionTitle(source);
	return (
		<EventCard
			expanded={props.expanded}
			setExpanded={props.expandCard}
			detailsFunction={props.detailsFunction}
			sid={sid}
			title={source}
			date={new Date(eventDate)}
			message={message}
			messageViewer={(source: string, message: string, sid?: string, loadJson?: (sid: string) => () => Promise<any>) =>
				messageViewer(source, message, props.index, sid, loadJson, props.virtualized)
			}
			loadMessageFunction={props.loadJson}
			secondaryAction={
				secondaryFunctionValue &&
				secondaryFunctionValueTitle && (
					<ViewJsonDialog
						title={camelToTitleCase(secondaryFunctionValueTitle)}
						json={tryJsonParse(secondaryFunctionValue)}
						label={camelToTitleCase(secondaryFunctionValueTitle)}
					></ViewJsonDialog>
				)
			}
			externalLinks={getExternalLinks(props.log, innerProperties)}
			innerCard={innerProperties}
		></EventCard>
	);
};

/**
 * Accepts a list of LogEvents that will be made
 * into EventCards
 * @param data
 */
export const LogListContainer = (data: LogListContainerProps) => {
	const expandedContainer = {};
	cache = new CellMeasurerCache({
		defaultHeight: 400,
		fixedWidth: true,
	});

	function rowRenderer(props: any) {
		const {
			key, // Unique key within array of rows
			index, // Index of row within collection
			style, // Style object to be applied to row (to position it)
			parent, //The parent List component
		} = props;
		return (
			<CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
				<div style={style} key={key}>
					<Row
						log={data.logs[index]}
						expandCard={(expanded: boolean) => {
							expandedContainer[index] = expanded;
							cache.clear(index, 0);
							list.recomputeRowHeights(index);
						}}
						expanded={expandedContainer[index] || false}
						detailsFunction={data.renderDetailsFunction}
						index={index}
						loadJson={data.loadJson}
						virtualized={true}
					/>
				</div>
			</CellMeasurer>
		);
	}
	return (
		<div style={{ minHeight: generateTableHeight(data.logs.length) - 1 }}>
			{
				//If the log list is 10 Items or more we use the react-virtualized list component to help alleviate performance problems.
				// otherwise we render the list using the mapping function.
				data.logs.length > 9 ? (
					<AutoSizer
						onResize={() => {
							cache.clearAll();
						}}
					>
						{({ height, width }) => (
							<List
								style={{
									outline: "none",
									minHeight: MIN_TABLE_HEIGHT,
								}}
								width={width}
								height={height}
								deferredMeasurementCache={cache}
								rowCount={data.logs.length}
								rowHeight={cache.rowHeight}
								estimatedRowSize={125}
								rowRenderer={rowRenderer}
								overscanRowCount={10}
								ref={(ref) => {
									list = ref;
								}}
							/>
						)}
					</AutoSizer>
				) : (
					<div style={{ marginTop: 10, width: "100%", overflow: "auto" }}>
						{data.logs.map((log, index) => (
							<div key={index} style={{ width: "100%" }}>
								<Row
									log={log}
									detailsFunction={data.renderDetailsFunction}
									expandCard={(expanded: boolean) => {
										expandedContainer[index] = expanded;
									}}
									expanded={expandedContainer[index] || false}
									index={index}
									loadJson={data.loadJson}
								/>
							</div>
						))}
					</div>
				)
			}
		</div>
	);
};
